/**
 * TrialCommandCenter — Operations Console > Trial Management.
 *
 * Iteration 71.
 *
 * Surfaces the trial dashboard, funnel, and per-trial detail.
 * Platform-admin only.
 *
 * Endpoints:
 *   GET    /api/trials/dashboard
 *   GET    /api/trials/list?filter=...
 *   GET    /api/trials/plans
 *   GET    /api/trials/{id}
 *   POST   /api/trials                              create
 *   POST   /api/trials/{id}/extend                  {days_added, reason}
 *   POST   /api/trials/{id}/convert                 {target_plan_code}
 *   POST   /api/trials/{id}/expire
 *   POST   /api/trials/{id}/archive
 *   POST   /api/trials/{id}/reopen                  {days}
 *   POST   /api/trials/{id}/recompute-usage
 *   GET    /api/trials/{id}/activity
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import {
  Hourglass, Plus, Search, RefreshCw, Loader2, AlertTriangle,
  CheckCircle2, XCircle, Flame, ArrowRight, Calendar, Users,
  FileStack, HardDrive, Activity, ChevronRight, X,
} from "lucide-react";
import { toast } from "sonner";

const STAGE_LABEL = {
  trial: "Trial", active: "Customer", archived: "Archived",
};
const HEALTH_TONE = {
  new:             { bg: "rgba(125,211,252,0.10)", fg: "#7dd3fc", border: "rgba(125,211,252,0.45)" },
  healthy:         { bg: "rgba(134,239,172,0.10)", fg: "#86efac", border: "rgba(134,239,172,0.45)" },
  active:          { bg: "rgba(134,239,172,0.08)", fg: "#86efac", border: "rgba(134,239,172,0.30)" },
  needs_attention: { bg: "rgba(251,191,36,0.10)", fg: "#fbbf24", border: "rgba(251,191,36,0.45)" },
  at_risk:         { bg: "rgba(252,165,165,0.10)", fg: "#fca5a5", border: "rgba(252,165,165,0.45)" },
  expiring_soon:   { bg: "rgba(249,115,22,0.10)", fg: "#fb923c", border: "rgba(249,115,22,0.45)" },
  converted:       { bg: "rgba(167,243,208,0.10)", fg: "#a7f3d0", border: "rgba(167,243,208,0.45)" },
  expired:         { bg: "rgba(203,213,225,0.10)", fg: "#cbd5e1", border: "rgba(203,213,225,0.45)" },
};

const fmt = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(undefined, { dateStyle: "medium" }); }
  catch { return "—"; }
};
const fmtDateTime = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }); }
  catch { return "—"; }
};

const FILTERS = [
  { id: "",            label: "All" },
  { id: "active",      label: "Active" },
  { id: "expiring_3d", label: "Expiring 3d" },
  { id: "expiring_7d", label: "Expiring 7d" },
  { id: "expired",     label: "Expired" },
  { id: "converted",   label: "Converted (30d)" },
];

export default function TrialCommandCenter() {
  const [dashboard, setDashboard] = useState(null);
  const [trials, setTrials] = useState([]);
  const [filter, setFilter] = useState("");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [openTrialId, setOpenTrialId] = useState(null);
  const [showCreate, setShowCreate] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [d, l] = await Promise.all([
        api.get("/trials/dashboard"),
        api.get("/trials/list", { params: filter ? { filter } : {} }),
      ]);
      setDashboard(d.data);
      setTrials(l.data.trials || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load Trial Command Center");
    } finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return trials;
    return trials.filter((t) =>
      (t.display_name || "").toLowerCase().includes(needle) ||
      (t.contact_email || "").toLowerCase().includes(needle) ||
      (t.slug || "").toLowerCase().includes(needle)
    );
  }, [trials, q]);

  const hotProspects = useMemo(() => trials.filter((t) => t.is_hot_prospect), [trials]);

  const tiles = dashboard?.tiles || {};
  const funnel = dashboard?.funnel || {};

  return (
    <div className="rounded-xl border bg-[var(--fop-surface)]"
         style={{ borderColor: "var(--fop-border)" }}
         data-testid="trial-command-center">
      <div className="px-5 py-4 border-b flex items-center justify-between gap-3 flex-wrap"
           style={{ borderColor: "var(--fop-border)" }}>
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-md inline-flex items-center justify-center"
               style={{ background: "rgba(249,115,22,0.10)", border: "1px solid rgba(249,115,22,0.35)" }}>
            <Hourglass size={16} className="text-[var(--fop-primary)]" />
          </div>
          <div>
            <div className="text-[var(--fop-text)] font-semibold">Trial Management</div>
            <div className="text-xs text-[var(--fop-text-3)] font-mono uppercase tracking-wider">
              Customer acquisition · conversion engine
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={load} disabled={loading}
                  className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md border text-xs uppercase tracking-wider font-mono"
                  style={{ borderColor: "var(--fop-border)", color: "var(--fop-text-2)" }}
                  data-testid="trials-refresh">
            <RefreshCw size={13} /> Refresh
          </button>
          <button onClick={() => setShowCreate(true)}
                  className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md text-xs uppercase tracking-wider font-mono text-white"
                  style={{ background: "rgba(249,115,22,0.85)" }}
                  data-testid="trials-create-open">
            <Plus size={13} /> Issue trial
          </button>
        </div>
      </div>

      {/* Dashboard tiles */}
      <div className="px-5 py-4 border-b grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2.5"
           style={{ borderColor: "var(--fop-border)" }} data-testid="trials-dashboard-tiles">
        <Tile label="Active trials"   value={tiles.active_trials ?? 0}  color="#7dd3fc" />
        <Tile label="Expiring 3d"     value={tiles.expiring_3d ?? 0}    color="#fb923c"
              onClick={() => setFilter("expiring_3d")} active={filter === "expiring_3d"}
              testid="trials-tile-expiring-3d" />
        <Tile label="Expiring 7d"     value={tiles.expiring_7d ?? 0}    color="#fbbf24"
              onClick={() => setFilter("expiring_7d")} active={filter === "expiring_7d"}
              testid="trials-tile-expiring-7d" />
        <Tile label="Expired"         value={tiles.expired ?? 0}        color="#fca5a5"
              onClick={() => setFilter("expired")} active={filter === "expired"}
              testid="trials-tile-expired" />
        <Tile label="Converted 30d"   value={tiles.converted_30d ?? 0}  color="#86efac"
              onClick={() => setFilter("converted")} active={filter === "converted"}
              testid="trials-tile-converted" />
        <Tile label="Conversion rate" value={`${Math.round((tiles.conversion_rate || 0) * 100)}%`} color="#a7f3d0" />
      </div>

      {/* Funnel */}
      <div className="px-5 py-3 border-b text-xs font-mono uppercase tracking-[0.18em] text-[var(--fop-text-3)]"
           style={{ borderColor: "var(--fop-border)" }} data-testid="trials-funnel">
        <div className="flex items-center gap-3 flex-wrap">
          <FunnelStep label="Demo requests"   v={funnel.demo_requests ?? 0} />
          <ChevronRight size={13} />
          <FunnelStep label="Trials created"  v={funnel.trials_created ?? 0} />
          <ChevronRight size={13} />
          <FunnelStep label="Trials activated" v={funnel.trials_activated ?? 0} />
          <ChevronRight size={13} />
          <FunnelStep label="Trials completed" v={funnel.trials_completed ?? 0} />
          <ChevronRight size={13} />
          <FunnelStep label="Customers won"   v={funnel.customers_won ?? 0} highlight />
        </div>
      </div>

      {/* Hot prospects strip */}
      {hotProspects.length > 0 && (
        <div className="px-5 py-3 border-b flex items-center gap-2 flex-wrap text-xs"
             style={{ borderColor: "var(--fop-border)" }} data-testid="trials-hot-prospects">
          <Flame size={14} className="text-[var(--fop-primary)]" />
          <span className="font-mono uppercase tracking-[0.18em] text-[var(--fop-text-3)]">
            Hot prospects ({hotProspects.length})
          </span>
          {hotProspects.slice(0, 5).map((t) => (
            <button key={t.id} onClick={() => setOpenTrialId(t.id)}
                    className="text-[var(--fop-text)] hover:text-[var(--fop-primary)] underline-offset-2 hover:underline"
                    data-testid={`trials-hot-${t.id}`}>
              {t.display_name}
            </button>
          ))}
        </div>
      )}

      {/* Filter pills + search */}
      <div className="px-5 py-3 border-b flex items-center gap-2 flex-wrap"
           style={{ borderColor: "var(--fop-border)" }}>
        {FILTERS.map((f) => (
          <button key={f.id || "all"} onClick={() => setFilter(f.id)}
                  className="h-8 px-3 rounded-full border text-xs uppercase tracking-wider font-mono"
                  style={{
                    borderColor: filter === f.id ? "var(--fop-primary)" : "var(--fop-border)",
                    background:  filter === f.id ? "rgba(249,115,22,0.10)" : "transparent",
                    color: filter === f.id ? "var(--fop-primary)" : "var(--fop-text-2)",
                  }}
                  data-testid={`trials-filter-${f.id || "all"}`}>
            {f.label}
          </button>
        ))}
        <div className="ml-auto relative">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--fop-text-3)]" />
          <input value={q} onChange={(e) => setQ(e.target.value)}
                  placeholder="Search company / email / slug"
                  className="h-8 pl-7 pr-3 rounded-md border bg-transparent text-sm w-64"
                  style={{ borderColor: "var(--fop-border)", color: "var(--fop-text)" }}
                  data-testid="trials-search" />
        </div>
      </div>

      {/* List */}
      <div data-testid="trials-list">
        {loading ? (
          <div className="p-8 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto text-[var(--fop-text-3)]" /></div>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-center text-[var(--fop-text-3)] text-sm" data-testid="trials-empty">
            No trials match this filter.
          </div>
        ) : (
          <div className="divide-y" style={{ borderColor: "var(--fop-border)" }}>
            {filtered.map((t) => (
              <TrialRow key={t.id} t={t} onClick={() => setOpenTrialId(t.id)} />
            ))}
          </div>
        )}
      </div>

      {openTrialId && (
        <TrialDetailDrawer
          tenantId={openTrialId}
          onClose={() => { setOpenTrialId(null); load(); }}
        />
      )}
      {showCreate && (
        <CreateTrialDialog
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); load(); }}
        />
      )}
    </div>
  );
}

function Tile({ label, value, color, onClick, active, testid }) {
  const C = onClick ? "button" : "div";
  return (
    <C onClick={onClick}
        className="rounded-md p-3 text-left border transition"
        style={{
          borderColor: active ? color : "var(--fop-border)",
          background:  active ? `${color}1A` : "rgba(15,23,42,0.4)",
          cursor: onClick ? "pointer" : "default",
        }}
        data-testid={testid}>
      <div className="text-[11px] font-mono uppercase tracking-[0.18em]"
           style={{ color }}>{label}</div>
      <div className="text-2xl font-semibold text-[var(--fop-text)] mt-1">{value}</div>
    </C>
  );
}

function FunnelStep({ label, v, highlight }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="text-[var(--fop-text-3)]">{label}</span>
      <span className={`font-semibold ${highlight ? "text-[var(--fop-primary)]" : "text-[var(--fop-text)]"}`}>{v}</span>
    </span>
  );
}

function TrialRow({ t, onClick }) {
  const tone = HEALTH_TONE[t.trial_health_status] || HEALTH_TONE.new;
  const isTrial = t.lifecycle_stage === "trial";
  const isConverted = !!t.trial_converted_at;
  const daysLeft = t.days_until_expiry;
  return (
    <button onClick={onClick}
            className="w-full px-5 py-3 flex items-start gap-3 hover:bg-white/[0.02] transition text-left"
            data-testid={`trial-row-${t.id}`}>
      <div className="w-9 h-9 rounded-md inline-flex items-center justify-center mt-0.5"
           style={{ background: tone.bg, border: `1px solid ${tone.border}` }}>
        <Hourglass size={14} style={{ color: tone.fg }} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="text-sm text-[var(--fop-text)] font-medium truncate">{t.display_name}</div>
          {t.is_hot_prospect && (
            <span className="inline-flex items-center gap-1 text-[11px] font-mono uppercase tracking-wider text-[var(--fop-primary)]">
              <Flame size={11} /> Hot
            </span>
          )}
          <span className="text-[11px] font-mono uppercase tracking-[0.18em]"
                style={{ color: tone.fg }}>
            {(t.trial_health_status || "new").replace(/_/g, " ")}
          </span>
        </div>
        <div className="text-xs text-[var(--fop-text-3)] font-mono mt-0.5 truncate">
          {t.contact_email || "—"} · {STAGE_LABEL[t.lifecycle_stage] || t.lifecycle_stage} · {t.plan_code || "—"}
        </div>
        <div className="text-xs text-[var(--fop-text-2)] mt-1 flex items-center gap-3 flex-wrap">
          {isTrial && (
            <span className="inline-flex items-center gap-1">
              <Calendar size={11} className="text-[var(--fop-text-3)]" />
              {daysLeft != null
                ? (daysLeft >= 0 ? `${daysLeft} day(s) left` : `expired ${-daysLeft} day(s) ago`)
                : "—"}
            </span>
          )}
          {isConverted && (
            <span className="inline-flex items-center gap-1 text-emerald-300">
              <CheckCircle2 size={11} /> converted {fmt(t.trial_converted_at)}
            </span>
          )}
          {t.usage?.counts && (
            <>
              <span className="inline-flex items-center gap-1">
                <Users size={11} className="text-[var(--fop-text-3)]" />
                {t.usage.counts.users || 0}
              </span>
              <span className="inline-flex items-center gap-1">
                <FileStack size={11} className="text-[var(--fop-text-3)]" />
                {t.usage.counts.sheets || 0}
              </span>
              <span className="inline-flex items-center gap-1">
                <HardDrive size={11} className="text-[var(--fop-text-3)]" />
                {(t.usage.storage_gb || 0).toFixed(2)} GB
              </span>
            </>
          )}
          {t.entitlements?.blocked_count > 0 && (
            <span className="inline-flex items-center gap-1 text-red-300">
              <XCircle size={11} /> {t.entitlements.blocked_count} blocked
            </span>
          )}
          {t.entitlements?.warning_count > 0 && (
            <span className="inline-flex items-center gap-1 text-amber-300">
              <AlertTriangle size={11} /> {t.entitlements.warning_count} warning
            </span>
          )}
        </div>
      </div>
      <ChevronRight size={16} className="text-[var(--fop-text-3)] mt-2" />
    </button>
  );
}

function TrialDetailDrawer({ tenantId, onClose }) {
  const [detail, setDetail] = useState(null);
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [showExtend, setShowExtend] = useState(false);
  const [showConvert, setShowConvert] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [d, a] = await Promise.all([
        api.get(`/trials/${tenantId}`),
        api.get(`/trials/${tenantId}/activity`, { params: { limit: 50 } }),
      ]);
      setDetail(d.data);
      setActivity(a.data.activity || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load trial detail");
    } finally { setLoading(false); }
  }, [tenantId]);

  useEffect(() => { load(); }, [load]);

  const recompute = async () => {
    setBusy(true);
    try {
      await api.post(`/trials/${tenantId}/recompute-usage`);
      toast.success("Usage recomputed");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const expire = async () => {
    if (!window.confirm("Force-expire this trial?")) return;
    setBusy(true);
    try {
      await api.post(`/trials/${tenantId}/expire`);
      toast.success("Trial expired");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const archive = async () => {
    if (!window.confirm("Archive this trial? The tenant becomes inaccessible.")) return;
    setBusy(true);
    try {
      await api.post(`/trials/${tenantId}/archive`);
      toast.success("Trial archived");
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const reopen = async () => {
    setBusy(true);
    try {
      await api.post(`/trials/${tenantId}/reopen`, { days: 14 });
      toast.success("Trial reopened (+14 days)");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const t = detail?.tenant;
  const u = detail?.usage;
  const ent = detail?.entitlements;
  const health = detail?.health;
  const plan = detail?.plan;

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[60] flex justify-end"
         data-testid="trial-detail-drawer" onClick={onClose}>
      <div className="w-full max-w-[680px] h-full overflow-y-auto bg-[var(--fop-surface)] border-l"
           style={{ borderColor: "var(--fop-border)" }} onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b sticky top-0 bg-[var(--fop-surface)] z-10 flex items-center justify-between"
             style={{ borderColor: "var(--fop-border)" }}>
          <div className="flex items-center gap-3 flex-wrap">
            <div className="text-xs font-mono uppercase tracking-[0.18em] text-[var(--fop-text-3)]">Trial Detail</div>
            <Link to={`/platform/tenants/${tenantId}`}
                   className="text-[11px] font-mono uppercase tracking-[0.18em] text-[var(--fop-primary)] hover:underline"
                   data-testid="trial-detail-open-command-center">
              Open command center →
            </Link>
          </div>
          <div className="min-w-0">
            <div className="text-[var(--fop-text)] font-semibold truncate">{t?.display_name || "…"}</div>
          </div>
          <button onClick={onClose} className="text-[var(--fop-text-3)] hover:text-[var(--fop-text)] p-1"
                  data-testid="trial-detail-close"><X size={18} /></button>
        </div>

        {loading || !t ? (
          <div className="p-10 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto text-[var(--fop-text-3)]" /></div>
        ) : (
          <div className="p-5 space-y-5">
            {/* Action bar */}
            <div className="flex items-center gap-2 flex-wrap">
              {t.lifecycle_stage === "trial" && (
                <>
                  <button onClick={() => setShowExtend(true)} disabled={busy}
                          className="h-9 px-3 rounded-md border text-xs uppercase tracking-wider font-mono"
                          style={{ borderColor: "var(--fop-border)", color: "var(--fop-text-2)" }}
                          data-testid="trial-action-extend">Extend trial</button>
                  <button onClick={() => setShowConvert(true)} disabled={busy}
                          className="h-9 px-3 rounded-md text-xs uppercase tracking-wider font-mono text-white"
                          style={{ background: "rgba(249,115,22,0.85)" }}
                          data-testid="trial-action-convert">Convert to customer</button>
                  <button onClick={expire} disabled={busy}
                          className="h-9 px-3 rounded-md border text-xs uppercase tracking-wider font-mono"
                          style={{ borderColor: "var(--fop-border)", color: "#fca5a5" }}
                          data-testid="trial-action-expire">Force expire</button>
                </>
              )}
              {(t.lifecycle_stage === "archived" || t.trial_expired_at) && (
                <button onClick={reopen} disabled={busy}
                        className="h-9 px-3 rounded-md border text-xs uppercase tracking-wider font-mono"
                        style={{ borderColor: "var(--fop-border)", color: "#7dd3fc" }}
                        data-testid="trial-action-reopen">Reopen (+14d)</button>
              )}
              <button onClick={archive} disabled={busy}
                      className="h-9 px-3 rounded-md border text-xs uppercase tracking-wider font-mono"
                      style={{ borderColor: "var(--fop-border)", color: "#fca5a5" }}
                      data-testid="trial-action-archive">Archive</button>
              <button onClick={recompute} disabled={busy}
                      className="h-9 px-3 rounded-md border text-xs uppercase tracking-wider font-mono ml-auto"
                      style={{ borderColor: "var(--fop-border)", color: "var(--fop-text-2)" }}
                      data-testid="trial-action-recompute">Recompute usage</button>
            </div>

            <Section title="Company">
              <Row label="Display name" v={t.display_name} />
              <Row label="Slug"   v={t.slug} mono />
              <Row label="Contact email" v={t.contact_email || "—"} />
              <Row label="Contact phone" v={t.contact_phone || "—"} />
              <Row label="Industry" v={t.industry || "—"} />
            </Section>

            <Section title="Trial">
              <Row label="Stage"   v={STAGE_LABEL[t.lifecycle_stage] || t.lifecycle_stage} />
              <Row label="Plan"    v={`${plan?.label || t.plan_code || "—"}${plan?.is_trial ? " (trial)" : ""}`} />
              <Row label="Started" v={fmtDateTime(t.trial_started_at)} />
              <Row label="Expires" v={fmtDateTime(t.trial_expires_at)} />
              <Row label="Days remaining" v={
                health?.days_until_expiry == null ? "—" :
                health.days_until_expiry >= 0 ? `${health.days_until_expiry} day(s)` :
                `expired ${-health.days_until_expiry} day(s) ago`
              } />
              <Row label="Extensions" v={`${t.trial_extended_count || 0}`} />
              {t.trial_converted_at && <Row label="Converted" v={`${fmtDateTime(t.trial_converted_at)} by ${t.trial_converted_by}`} />}
              {(t.trial_extension_history || []).length > 0 && (
                <div className="mt-2 text-xs space-y-1 font-mono">
                  {(t.trial_extension_history || []).map((ev, i) => (
                    <div key={i} className="text-[var(--fop-text-3)]">
                      +{ev.days_added}d by {ev.extended_by} on {fmt(ev.at)}{ev.reason ? ` — ${ev.reason}` : ""}
                    </div>
                  ))}
                </div>
              )}
            </Section>

            <Section title="Usage (30-day window)">
              <Row label="Projects" v={u?.counts?.projects ?? 0} />
              <Row label="Users (active / total)" v={`${u?.counts?.active_users ?? 0} / ${u?.counts?.users ?? 0}`} />
              <Row label="Sheets" v={u?.counts?.sheets ?? 0} />
              <Row label="Storage" v={`${(u?.storage_gb ?? 0).toFixed(2)} GB`} />
              <Row label="Logins (30d)" v={u?.logins_30d ?? 0} />
              <Row label="Activity (30d)" v={u?.activity_30d ?? 0} />
              <Row label="Last computed" v={fmtDateTime(u?.computed_at)} mono />
            </Section>

            <Section title="Entitlements">
              {(ent?.rows || []).map((r) => {
                const tone = r.status === "blocked" ? "#fca5a5"
                           : r.status === "warning" ? "#fbbf24"
                           : "#86efac";
                return (
                  <div key={r.limit} className="flex items-center justify-between py-1.5 border-b last:border-b-0"
                       style={{ borderColor: "var(--fop-border)" }}
                       data-testid={`trial-entitlement-${r.limit}`}>
                    <div className="text-sm text-[var(--fop-text-2)] capitalize">{r.limit.replace(/_/g, " ")}</div>
                    <div className="text-xs font-mono">
                      <span style={{ color: tone }}>{r.current}</span>
                      <span className="text-[var(--fop-text-3)]"> / {r.unlimited ? "∞" : r.cap}</span>
                    </div>
                  </div>
                );
              })}
              {(ent?.rows || []).filter((r) => r.friendly_message).map((r) => (
                <div key={`m-${r.limit}`} className="mt-2 text-xs p-2 rounded-md"
                     style={{ background: "rgba(249,115,22,0.06)", border: "1px solid rgba(249,115,22,0.30)", color: "#fed7aa" }}>
                  {r.friendly_message}
                </div>
              ))}
            </Section>

            <Section title="Health (overlay)">
              {health ? (
                <>
                  <Row label="Status" v={health.trial_health_status?.replace(/_/g, " ")} />
                  <Row label="Score"  v={`${health.score} / 100`} />
                  {(health.risk_flags || []).length > 0 && (
                    <ul className="text-xs mt-2 space-y-1">
                      {health.risk_flags.map((r, i) => (
                        <li key={i} className="text-red-300">▸ {r.text}</li>
                      ))}
                    </ul>
                  )}
                  {(health.positive_signals || []).length > 0 && (
                    <ul className="text-xs mt-2 space-y-1">
                      {health.positive_signals.map((r, i) => (
                        <li key={i} className="text-emerald-300">▸ {r.text}</li>
                      ))}
                    </ul>
                  )}
                </>
              ) : <div className="text-xs text-[var(--fop-text-3)]">No health data yet.</div>}
            </Section>

            <Section title="Activity (50 most recent)">
              {activity.length === 0 ? (
                <div className="text-xs text-[var(--fop-text-3)]">No audited activity yet.</div>
              ) : (
                <div className="text-xs space-y-1 max-h-72 overflow-y-auto pr-1">
                  {activity.map((a, i) => (
                    <div key={i} className="flex items-start gap-2">
                      <Activity size={11} className="text-[var(--fop-text-3)] mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <div className="text-[var(--fop-text-2)] font-mono truncate">{a.action}</div>
                        <div className="text-[var(--fop-text-3)] font-mono text-[10px]">
                          {fmtDateTime(a.created_at)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Section>
          </div>
        )}

        {showExtend && (
          <ExtendDialog tenantId={tenantId} onClose={() => setShowExtend(false)}
                          onDone={() => { setShowExtend(false); load(); }} />
        )}
        {showConvert && (
          <ConvertDialog tenantId={tenantId} onClose={() => setShowConvert(false)}
                            onDone={() => { setShowConvert(false); load(); }} />
        )}
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-[var(--fop-text-3)] mb-2">{title}</div>
      <div className="rounded-md border p-3" style={{ borderColor: "var(--fop-border)", background: "rgba(15,23,42,0.4)" }}>
        {children}
      </div>
    </div>
  );
}

function Row({ label, v, mono }) {
  return (
    <div className="flex items-center justify-between gap-3 py-1">
      <div className="text-xs text-[var(--fop-text-3)] font-mono uppercase tracking-wider">{label}</div>
      <div className={`text-sm text-[var(--fop-text)] ${mono ? "font-mono text-xs" : ""} text-right`}>{v ?? "—"}</div>
    </div>
  );
}

function ExtendDialog({ tenantId, onClose, onDone }) {
  const [days, setDays] = useState(7);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (override) => {
    setBusy(true);
    try {
      const d = override ?? days;
      await api.post(`/trials/${tenantId}/extend`, { days_added: d, reason });
      toast.success(`Extended by ${d} day(s)`);
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <ModalShell title="Extend trial" onClose={onClose} testid="trial-extend-dialog">
      <div className="flex items-center gap-2">
        <button onClick={() => submit(7)} disabled={busy}
                className="h-9 px-3 rounded-md border text-xs uppercase tracking-wider font-mono"
                style={{ borderColor: "var(--fop-border)", color: "var(--fop-text-2)" }}
                data-testid="trial-extend-7">+7 days</button>
        <button onClick={() => submit(14)} disabled={busy}
                className="h-9 px-3 rounded-md border text-xs uppercase tracking-wider font-mono"
                style={{ borderColor: "var(--fop-border)", color: "var(--fop-text-2)" }}
                data-testid="trial-extend-14">+14 days</button>
      </div>
      <div className="space-y-2 mt-3">
        <Label>Custom days (1–90)</Label>
        <input type="number" value={days} onChange={(e) => setDays(parseInt(e.target.value || "0"))}
               className="h-10 px-3 rounded-md border bg-transparent w-32"
               style={{ borderColor: "var(--fop-border)", color: "var(--fop-text)" }}
               data-testid="trial-extend-custom-days" />
        <Label>Reason (optional)</Label>
        <input value={reason} onChange={(e) => setReason(e.target.value)}
               className="h-10 px-3 rounded-md border bg-transparent w-full"
               style={{ borderColor: "var(--fop-border)", color: "var(--fop-text)" }}
               data-testid="trial-extend-reason" />
      </div>
      <div className="flex items-center gap-2 mt-3">
        <button onClick={() => submit()} disabled={busy || !days || days < 1}
                className="h-9 px-3 rounded-md text-xs uppercase tracking-wider font-mono text-white"
                style={{ background: "rgba(249,115,22,0.85)" }}
                data-testid="trial-extend-submit">Apply</button>
        <button onClick={onClose} className="text-xs text-[var(--fop-text-3)] hover:text-[var(--fop-text)]">Cancel</button>
      </div>
    </ModalShell>
  );
}

function ConvertDialog({ tenantId, onClose, onDone }) {
  const [plans, setPlans] = useState([]);
  const [target, setTarget] = useState("professional");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/trials/plans").then(({ data }) =>
      setPlans((data.plans || []).filter((p) => !p.is_trial))).catch(() => {});
  }, []);
  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/trials/${tenantId}/convert`, { target_plan_code: target });
      toast.success("Trial converted to customer");
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <ModalShell title="Convert to customer" onClose={onClose} testid="trial-convert-dialog">
      <p className="text-xs text-[var(--fop-text-3)] mb-3">
        In-place conversion. No data migration. Users, projects, plans, history,
        onboarding, health and communications are all preserved.
      </p>
      <Label>Target plan</Label>
      <select value={target} onChange={(e) => setTarget(e.target.value)}
              className="h-10 px-3 rounded-md border bg-[var(--fop-surface)] w-full"
              style={{ borderColor: "var(--fop-border)", color: "var(--fop-text)" }}
              data-testid="trial-convert-plan">
        {plans.map((p) => <option key={p.code} value={p.code}>{p.label}</option>)}
      </select>
      <div className="flex items-center gap-2 mt-3">
        <button onClick={submit} disabled={busy}
                className="h-9 px-3 rounded-md text-xs uppercase tracking-wider font-mono text-white"
                style={{ background: "rgba(249,115,22,0.85)" }}
                data-testid="trial-convert-submit">Convert</button>
        <button onClick={onClose} className="text-xs text-[var(--fop-text-3)] hover:text-[var(--fop-text)]">Cancel</button>
      </div>
    </ModalShell>
  );
}

function CreateTrialDialog({ onClose, onCreated }) {
  const [form, setForm] = useState({
    display_name: "", slug: "", initial_owner_name: "",
    initial_owner_email: "", contact_phone: "", industry: "heavy_civil",
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async (e) => {
    e?.preventDefault();
    setBusy(true);
    try {
      const slug = form.slug.trim() || form.display_name.toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 40);
      await api.post("/trials", { ...form, slug });
      toast.success("Trial issued");
      onCreated();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <ModalShell title="Issue trial" onClose={onClose} testid="create-trial-dialog">
      <form onSubmit={submit} className="space-y-3">
        <Field label="Company name" value={form.display_name}
                onChange={(v) => set("display_name", v)} required testid="create-trial-display-name" />
        <Field label="Slug (URL-safe; auto-derived if blank)" value={form.slug}
                onChange={(v) => set("slug", v)} mono testid="create-trial-slug" />
        <Field label="Owner name" value={form.initial_owner_name}
                onChange={(v) => set("initial_owner_name", v)} required testid="create-trial-owner-name" />
        <Field label="Owner email" value={form.initial_owner_email}
                onChange={(v) => set("initial_owner_email", v)} required type="email" testid="create-trial-owner-email" />
        <Field label="Phone (optional)" value={form.contact_phone}
                onChange={(v) => set("contact_phone", v)} testid="create-trial-phone" />
        <Field label="Industry" value={form.industry}
                onChange={(v) => set("industry", v)} testid="create-trial-industry" />
        <div className="flex items-center gap-2 pt-2">
          <button type="submit" disabled={busy}
                  className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md text-xs uppercase tracking-wider font-mono text-white"
                  style={{ background: "rgba(249,115,22,0.85)" }}
                  data-testid="create-trial-submit">
            {busy && <Loader2 size={13} className="animate-spin" />} Issue trial
          </button>
          <button type="button" onClick={onClose}
                  className="text-xs text-[var(--fop-text-3)] hover:text-[var(--fop-text)]">Cancel</button>
        </div>
      </form>
    </ModalShell>
  );
}

function ModalShell({ title, onClose, children, testid }) {
  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[70] flex items-center justify-center p-4"
         data-testid={testid} onClick={onClose}>
      <div className="bg-[var(--fop-surface)] border rounded-xl shadow-2xl w-full max-w-md p-5"
           style={{ borderColor: "var(--fop-border)" }} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <div className="text-[var(--fop-text)] font-semibold">{title}</div>
          <button onClick={onClose} className="text-[var(--fop-text-3)] hover:text-[var(--fop-text)]"><X size={16} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Label({ children }) {
  return <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-[var(--fop-text-3)]">{children}</div>;
}

function Field({ label, value, onChange, required, type = "text", mono, testid }) {
  return (
    <div className="space-y-1">
      <Label>{label}</Label>
      <input value={value} onChange={(e) => onChange(e.target.value)}
              required={required} type={type}
              className={`h-10 px-3 rounded-md border bg-transparent w-full ${mono ? "font-mono text-sm" : ""}`}
              style={{ borderColor: "var(--fop-border)", color: "var(--fop-text)" }}
              data-testid={testid} />
    </div>
  );
}
